// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseUploadFile, Loading } from '~/components/common';
// Others
import { IBaseFile } from '~/utils/interface/common';
import { schema } from '~/components/common/baseSelectFileModal/helper';
// Styles, images, icons
import styles from './BaseSelectFileModal.module.scss';
import { icons } from '~/assets';

type Props = {
  multiple?: boolean;
  isLoading?: boolean;
  onAdd: (files: IBaseFile[]) => void;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const BaseSelectFileModal = (props: Props) => {
  //#region Destructuring Props
  const { isLoading, multiple = true, onClose, onAdd } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ files: IBaseFile[] }>({
    resolver: yupResolver(schema(t)),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'files',
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleFileChange = (newFiles: IBaseFile[]) => {
    if (multiple) {
      append(newFiles);
      return;
    }

    if (fields.length > 0) {
      remove(fields.map((_, i) => i));
    }

    if (newFiles && newFiles.length > 0) {
      append([newFiles[0]]);
    }
  };

  const handleRemoveFile = (index: number) => {
    remove(index);
  };

  const handleAddFiles = (data: { files: IBaseFile[] }) => {
    onAdd(data.files);
    onClose();
  };
  //#endregion Handle Function

  return (
    <Dialog id='BaseSelectFileModalComponent' open={true} onClose={onClose} fullWidth maxWidth={'sm'}>
      <Box component={'form'} onSubmit={handleSubmit(handleAddFiles)}>
        <DialogTitle>{t('common_title_add_file')}</DialogTitle>

        <DialogContent dividers={true}>
          <Controller
            name='files'
            control={control}
            render={() => (
              <BaseUploadFile multiple={multiple} onChange={handleFileChange} errorMessage={errors.files?.message} />
            )}
          />

          <Stack direction='column' spacing={2} marginTop={2}>
            {fields.map((item, index) => (
              <Stack key={index} direction='column'>
                <Stack className={cx('fileItem')}>
                  <Stack direction='row' gap={4} className={cx('fileInfo')}>
                    <img src={icons.commonIconFile} alt={t('common_alt_img')} />

                    <Typography className={cx('fileName')}>{item?.file?.name}</Typography>
                  </Stack>

                  <IconButton onClick={() => handleRemoveFile(index)}>
                    <img src={icons.commonIconTrashGrey} alt={t('common_alt_img')} />
                  </IconButton>
                </Stack>
                <Typography className={cx('errorMessage')}>{errors.files?.[index]?.file?.message}</Typography>
              </Stack>
            ))}
          </Stack>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button className={cx('buttonCancel')} variant='outlined' onClick={onClose}>
            {t('common_action_cancel')}
          </Button>

          <Button variant='contained' className={cx('buttonAdd')} type='submit'>
            {t('common_action_add')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default BaseSelectFileModal;
